import { LencoTypography } from '@/atomic-components/LencoTypography';
import { IProductData } from '@/components/search-results/model';
import { Box, Drawer, IconButton } from '@mui/material';
import CloseIcon from '@/assets/CloseIcon.svg';
import FlexBox from '@/atomic-components/FlexBox';

import AddonProductTile from '@/components/AddonProductTile';
import LencoButton from '@/atomic-components/LencoButton';
import { getStyle } from '@/utils/theme/fonts';
import { cartService } from '@/services/cart';
import { useCart } from '@/hooks/useCart';
import { useState } from 'react';
import { getColor } from '@/utils/theme/colors';
import { useLencoDispatch } from '@/hooks/useLencoDispatch';
import { useNotification } from '@/hooks/useNotification';
import { NotificationSeverity } from '@/components/NotifyToast';
import { BaseAxiosApiError } from '@/models/generics';
import { TUpdateCartApiResponse } from '@/services/cart/models/update-cart';
import { useFullScreenLoader } from '@/hooks/useFullScreenLoader';
import { withFullScreenLoader } from '@/hoc/withFullScreenLoader';
import Link from 'next/link';

type PostAtcAddonsDrawerProps = {
	open: boolean;
	onClose: () => void;
	products: IProductData[];
	hideLoader: any;
	showLoader: any;
	tenureselect: any;
};

const PostAtcAddonsDrawer = (props: PostAtcAddonsDrawerProps) => {
	const { cart, cartActions } = useCart();
	const [addonBeingAdded, setAddonBeingAdded] = useState<number>();
	const [addonsAdded, setAddonsAdded] = useState<number[]>([]);
	const dispatch = useLencoDispatch();
	const {
		actions: { notifyUser },
	} = useNotification();
	const { loader } = useFullScreenLoader();

	const onItemAdded = (product: IProductData, res: TUpdateCartApiResponse) => {
		cartActions.getCart();
		setAddonsAdded([...addonsAdded, product.id]);
		notifyUser({
			message: res.data.data?.message,
			severity: NotificationSeverity.SUCCESS,
		});
	};

	const onAddItemError = (e: any) => {
		const error = e as BaseAxiosApiError;

		notifyUser({
			message: error.response?.data.message,
			severity: NotificationSeverity.SUCCESS,
		});
	};

	const addToCartClicked = (product: IProductData) => {
		let tenure = undefined;

		if (product.lineOfProduct == 'RENT') {
			tenure = props?.tenureselect;
		}

		setAddonBeingAdded(product.id);

		loader.show();

		cartService
			.addItemToCart(cart?.id!, {
				id: product.id,
				tenure,
			})
			.then((res) => onItemAdded(product, res))
			.catch(onAddItemError)
			.finally(() => {
				setAddonBeingAdded(undefined);
				loader.hide();
			});
	};

	const getFooterForAddon = (product: IProductData) => {
		const isProductAdded = addonsAdded.includes(product.id);

		if (isProductAdded) {
			return (
				<FlexBox
					alignItems='center'
					justifyContent='center'
					height='32px'
					onClick={(e) => {
						e.stopPropagation();
					}}
				>
					<LencoTypography
						color={getColor('Minty 0')}
						textAlign={'center'}
						variant='Small_Regular'
					>
						Added to cart
					</LencoTypography>
				</FlexBox>
			);
		} else {
			return (
				<LencoButton
					fullWidth
					variant='contained'
					sx={{ ...getStyle('Tiny_SemiBold') }}
					isLoading={addonBeingAdded == product.id}
					circularProgressProps={{
						size: 18,
					}}
					onClick={(e) => {
						e.preventDefault();
						addToCartClicked(product);
					}}
				>
					Add to cart
				</LencoButton>
			);
		}
	};

	return (
		<Drawer open={props.open} anchor='right' onClose={props.onClose}>
			<Box width='480px' p='40px' sx={{ background: getColor('Grey-7') }}>
				<FlexBox justifyContent={'space-between'} alignItems='center'>
					<LencoTypography variant='H9A_Regular'>
						Select Add ons
					</LencoTypography>
					<IconButton onClick={props.onClose}>
						<CloseIcon />
					</IconButton>
				</FlexBox>
				<FlexBox
					justifyContent='space-between'
					mt='48px'
					sx={{
						flexWrap: 'wrap',
					}}
				>
					{props.products?.map((product) => (
						<Link href={product.permalink} key={product.permalink}>
							<Box width='184px'>
								<AddonProductTile
									product={product}
									tenure={props?.tenureselect}
									Footer={<Box mt='8px'>{getFooterForAddon(product)}</Box>}
								/>
							</Box>
						</Link>
					))}
				</FlexBox>
			</Box>
		</Drawer>
	);
};

export default withFullScreenLoader(PostAtcAddonsDrawer);
